import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import TextareaAutosize from "react-textarea-autosize";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  BusinessReportPrompt,
  BusinessReportType,
  PromptDataType,
  isAuthoringTemplateType,
  isBusinessReportType,
} from "@/modules/snapshots/businessReports.types";
import {
  FAMILY_SUMMARY_MODE_OPTIONS,
  getFamilySummaryMode,
  getPromptEditDefaultValues,
  normalizePromptEditPayloadForSubmit,
  type PromptEditFormInputs,
} from "@/modules/snapshots/components/BusinessReportPromptEditModal.utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AUTO_REFRESH_OPTIONS, MAX_FAMILY_SUMMARY_PROMPT_CHARS } from "./BusinessReportPromptEditModal.constants";

const DATA_TYPE_OPTIONS: Array<{ value: PromptDataType | null; id: string; label: string; description: string }> = [
  {
    value: null,
    id: "free_text",
    label: "Free text",
    description: "Narrative answers that are not grouped in pivot tables",
  },
  {
    value: "categorical",
    id: "categorical",
    label: "Categorical",
    description: "Names, labels, statuses, and text categories",
  },
  {
    value: "numeric",
    id: "numeric",
    label: "Numerical",
    description: "Amounts, counts, percentages, and measured values",
  },
  {
    value: "date",
    id: "date",
    label: "Date",
    description: "Dates that can be grouped by week, month, or quarter",
  },
  {
    value: "currency",
    id: "currency",
    label: "Currency",
    description: "One amount and ISO 4217 currency code; not aggregated across currencies",
  },
  {
    value: "relevancy",
    id: "relevancy",
    label: "Relevancy",
    description:
      "Maps this prompt's Y/N/Unsure answer to True/False and stops later prompts when false; Batch templates add a separate gate layer",
  },
];

interface BusinessReportPromptEditModalProps {
  prompt: BusinessReportPrompt;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PromptEditFormInputs) => Promise<void>;
  isLoading?: boolean;
  templateType?: BusinessReportType;
  title?: string;
  submitLabel?: string;
}

const GENERIC_SAVE_ERROR = "Failed to save changes.";

const getPromptSaveErrorMessage = (error: unknown): string => {
  if (typeof error !== "object" || error === null) return GENERIC_SAVE_ERROR;

  const candidate = error as { status?: unknown; data?: unknown };
  if (candidate.status !== 422 || typeof candidate.data !== "object" || candidate.data === null) {
    return GENERIC_SAVE_ERROR;
  }

  const detail = (candidate.data as { detail?: unknown }).detail;
  return typeof detail === "string" && detail.trim() ? detail : GENERIC_SAVE_ERROR;
};

export const BusinessReportPromptEditModal: React.FC<BusinessReportPromptEditModalProps> = ({
  prompt,
  isOpen,
  onClose,
  onSave,
  isLoading = false,
  templateType = "snapshot_template",
  title = "Edit SnapShot Prompt",
  submitLabel = "Save Changes",
}) => {
  const isBusinessReport = isBusinessReportType(templateType);
  // Authoring checks are plain (heading, prompt) pairs: none of the snapshot retrieval options apply.
  const hasOptions = !isAuthoringTemplateType(templateType);

  const methods = useForm<PromptEditFormInputs>({
    defaultValues: getPromptEditDefaultValues(prompt),
  });
  const { reset } = methods;
  const familySummaryMode = getFamilySummaryMode(prompt);

  React.useEffect(() => {
    if (isOpen) {
      reset({
        name: prompt.name,
        prompt: prompt.prompt,
        full_context: prompt.full_context,
        is_date: prompt.is_date,
        data_type: prompt.data_type ?? (prompt.is_date ? "date" : null),
        use_image: prompt.use_image,
        no_retriever: prompt.no_retriever,
        web_search: prompt.web_search,
        traffic_light: prompt.traffic_light ?? false,
        auto_refresh_frequency: prompt.auto_refresh_frequency ?? null,
        family_summary_mode: familySummaryMode,
        family_summary_prompt: prompt.family_summary_prompt ?? null,
        family_metadata: prompt.family_metadata ?? false,
      });
    }
  }, [
    isOpen,
    reset,
    prompt.id,
    prompt.name,
    prompt.prompt,
    prompt.full_context,
    prompt.is_date,
    prompt.data_type,
    prompt.use_image,
    prompt.no_retriever,
    prompt.web_search,
    prompt.traffic_light,
    prompt.auto_refresh_frequency,
    familySummaryMode,
    prompt.family_summary_prompt,
    prompt.family_metadata,
  ]);

  const onSubmit = async (payload: PromptEditFormInputs) => {
    methods.clearErrors("root.serverError");
    const submitPayload = normalizePromptEditPayloadForSubmit(payload, isBusinessReport);

    try {
      await onSave(submitPayload);
      onClose();
      methods.reset(submitPayload);
    } catch (error) {
      methods.setError("root.serverError", {
        type: "server",
        message: getPromptSaveErrorMessage(error),
      });
    }
  };

  const handleClose = () => {
    methods.reset(getPromptEditDefaultValues(prompt));
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => (!open ? handleClose() : undefined)}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
          {/* Name Field */}
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              {...methods.register("name", { required: "Name is required" })}
              placeholder="Enter prompt name"
              className="w-full"
            />
            {methods.formState.errors.name && (
              <p className="text-sm text-red-600">{methods.formState.errors.name.message}</p>
            )}
          </div>

          {/* Prompt Field */}
          <div className="space-y-2">
            <Label htmlFor="prompt">Prompt</Label>
            <TextareaAutosize
              id="prompt"
              {...methods.register("prompt", { required: "Prompt is required" })}
              placeholder="Enter your prompt"
              className="min-h-[120px] w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              minRows={5}
            />
            {methods.formState.errors.prompt && (
              <p className="text-sm text-red-600">{methods.formState.errors.prompt.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {
                "Reference other prompt results using {{ref:Prompt Name}} syntax. You can only reference prompts that appear earlier in the list."
              }
            </p>
            <p className="text-xs text-muted-foreground">{"Insert the document's title using {{doc:title}}."}</p>
          </div>

          {methods.formState.errors.root?.serverError?.message && (
            <p role="alert" className="text-sm text-red-600">
              {methods.formState.errors.root.serverError.message}
            </p>
          )}

          {hasOptions && (
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Options</h4>

              {!isBusinessReport && (
                <>
                  <div className="space-y-2 rounded-md border border-input bg-muted/20 p-3">
                    <Label htmlFor="auto_refresh_frequency" className="text-sm font-medium">
                      Automatic refresh
                    </Label>
                    <Controller
                      control={methods.control}
                      name="auto_refresh_frequency"
                      render={({ field }) => (
                        <select
                          id="auto_refresh_frequency"
                          name={field.name}
                          ref={field.ref}
                          value={field.value ?? "off"}
                          disabled={methods.watch("web_search") || methods.watch("data_type") === "relevancy"}
                          onBlur={field.onBlur}
                          onChange={event => field.onChange(event.target.value === "off" ? null : event.target.value)}
                          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          {AUTO_REFRESH_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                    <p className="text-xs text-muted-foreground">
                      Refreshes completed SnapShots with the current prompt using the Batch API.
                    </p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="full_context"
                      checked={methods.watch("full_context")}
                      disabled={methods.watch("use_image") || methods.watch("no_retriever")}
                      onCheckedChange={checked => {
                        methods.setValue("full_context", !!checked);
                        if (checked) {
                          methods.setValue("use_image", false);
                          methods.setValue("no_retriever", false);
                          methods.setValue("web_search", false);
                        }
                      }}
                    />
                    <Label htmlFor="full_context" className="text-sm font-normal">
                      Use full document context
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="use_image"
                      checked={methods.watch("use_image")}
                      disabled={methods.watch("full_context") || methods.watch("no_retriever")}
                      onCheckedChange={checked => {
                        methods.setValue("use_image", !!checked);
                        if (checked) {
                          methods.setValue("full_context", false);
                          methods.setValue("no_retriever", false);
                          methods.setValue("web_search", false);
                        }
                      }}
                    />
                    <Label htmlFor="use_image" className="text-sm font-normal">
                      Use image-based analysis (multimodal)
                    </Label>
                  </div>
                </>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="no_retriever"
                  checked={methods.watch("no_retriever")}
                  disabled={!isBusinessReport && (methods.watch("full_context") || methods.watch("use_image"))}
                  onCheckedChange={checked => {
                    methods.setValue("no_retriever", !!checked);
                    if (checked && !isBusinessReport) {
                      methods.setValue("full_context", false);
                      methods.setValue("use_image", false);
                      methods.setValue("web_search", false);
                    }
                  }}
                />
                <Label htmlFor="no_retriever" className="text-sm font-normal">
                  No document retrieval (prompt only)
                </Label>
              </div>

              {!isBusinessReport && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="web_search"
                    checked={methods.watch("web_search")}
                    disabled={methods.watch("data_type") === "relevancy"}
                    onCheckedChange={checked => {
                      methods.setValue("web_search", !!checked);
                      if (checked) {
                        methods.setValue("full_context", false);
                        methods.setValue("use_image", false);
                        methods.setValue("no_retriever", false);
                        methods.setValue("auto_refresh_frequency", null);
                      }
                    }}
                  />
                  <Label htmlFor="web_search" className="text-sm font-normal">
                    Use Gemini Web Search
                  </Label>
                </div>
              )}

              {!isBusinessReport && (
                <div className="space-y-3">
                  <Label className="text-sm font-normal">Data type</Label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {DATA_TYPE_OPTIONS.map(option => {
                      const selectedDataType = methods.watch("data_type");
                      const isSelected = selectedDataType === option.value;
                      const isDisabled =
                        (methods.watch("traffic_light") && option.value !== "categorical") ||
                        (isBusinessReport && (option.value === "currency" || option.value === "relevancy"));

                      return (
                        <div
                          key={option.id}
                          className={`flex min-h-[92px] gap-3 rounded-md border p-3 transition-colors ${
                            isSelected
                              ? "border-primary bg-primary/5"
                              : isDisabled
                                ? "border-muted bg-muted/40 text-muted-foreground opacity-70"
                                : "border-input bg-background"
                          }`}
                        >
                          <Checkbox
                            id={`data_type_${option.id}`}
                            checked={isSelected}
                            disabled={isDisabled}
                            onCheckedChange={checked => {
                              if (!checked) return;

                              methods.setValue("data_type", option.value);
                              methods.setValue("is_date", option.value === "date");
                              if (
                                option.value === "date" ||
                                option.value === "currency" ||
                                option.value === "relevancy"
                              ) {
                                methods.setValue("traffic_light", false);
                              }
                              if (option.value === "relevancy") {
                                methods.setValue("web_search", false);
                                methods.setValue("auto_refresh_frequency", null);
                              }
                            }}
                          />
                          <Label htmlFor={`data_type_${option.id}`} className="cursor-pointer space-y-1 font-normal">
                            <span className="block text-sm font-medium">{option.label}</span>
                            <span className="block text-xs leading-4 text-muted-foreground">{option.description}</span>
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!isBusinessReport && (
                <div className="space-y-3 rounded-md border border-input bg-muted/20 p-3">
                  <div className="space-y-2">
                    <Label htmlFor="family_summary_mode" className="text-sm font-medium">
                      Family summary
                    </Label>
                    <Controller
                      control={methods.control}
                      name="family_summary_mode"
                      render={({ field }) => (
                        <select
                          id="family_summary_mode"
                          name={field.name}
                          ref={field.ref}
                          value={field.value}
                          onBlur={field.onBlur}
                          onChange={event => field.onChange(event.target.value)}
                          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          {FAMILY_SUMMARY_MODE_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                    <p className="text-xs text-muted-foreground">
                      How this column is filled when a contract family is consolidated into one summary row.
                    </p>
                  </div>
                  {methods.watch("family_summary_mode") === "summarise" && (
                    <div className="space-y-2">
                      <Label htmlFor="family_summary_prompt" className="text-sm font-medium">
                        Family summary prompt
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        The model&apos;s only instructions when this column is consolidated. Describe the input, the
                        rules and the JSON answer with a current_position key.
                      </p>
                      <TextareaAutosize
                        id="family_summary_prompt"
                        {...methods.register("family_summary_prompt", {
                          maxLength: {
                            value: MAX_FAMILY_SUMMARY_PROMPT_CHARS,
                            message: `The family summary prompt must be at most ${MAX_FAMILY_SUMMARY_PROMPT_CHARS.toLocaleString()} characters.`,
                          },
                          validate: value =>
                            methods.getValues("family_summary_mode") !== "summarise" ||
                            !!value?.trim() ||
                            "Enter the prompt used to summarise this column across a contract family.",
                        })}
                        aria-invalid={!!methods.formState.errors.family_summary_prompt}
                        className="min-h-[60px] w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        minRows={2}
                      />
                      {methods.formState.errors.family_summary_prompt?.message ? (
                        <p role="alert" className="text-xs text-red-700">
                          {methods.formState.errors.family_summary_prompt.message}
                        </p>
                      ) : null}
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="family_metadata"
                      role="switch"
                      aria-checked={methods.watch("family_metadata")}
                      checked={methods.watch("family_metadata")}
                      onCheckedChange={checked => methods.setValue("family_metadata", !!checked)}
                    />
                    <Label htmlFor="family_metadata" className="text-sm font-normal">
                      Family metadata
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Send this column&apos;s value as context with every other column&apos;s family summary.
                  </p>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="traffic_light"
                  checked={methods.watch("traffic_light")}
                  disabled={
                    !isBusinessReport && ["date", "currency", "relevancy"].includes(methods.watch("data_type") ?? "")
                  }
                  onCheckedChange={checked => {
                    methods.setValue("traffic_light", !!checked);
                    if (checked) {
                      methods.setValue("is_date", false);
                      methods.setValue("data_type", "categorical");
                    }
                  }}
                />
                <Label htmlFor="traffic_light" className="text-sm font-normal">
                  Traffic Light (returns HIGH / MEDIUM / LOW rating)
                </Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
